<script setup lang="ts">
import { computed, onMounted, type PropType, ref, watch } from 'vue'
import { type ObjectTreeItemType, ObjectTreeLink } from './../model/types'
import { useObjectTree } from './../model/model'
import { useConfirm, VIcons } from '@/shared/ui'
import ObjectsTreeIcon from './ObjectsTreeIcon.vue'
import { useRouter } from 'vue-router'
import ObjectsTreeCounter from './ObjectsTreeCounter.vue'
import { size } from 'lodash'
import { useModel as useMoveModel } from '@/features/Tree/TreeMove'
import { useTranslate } from '@/shared/lib'
import { useAuth } from '@/entities/Auth'

const props = defineProps({
  item: {
    type: Object as PropType<ObjectTreeItemType>,
    required: true,
  },
  parent: {
    type: Object as PropType<ObjectTreeItemType>,
  },
})

const open = ref(false)
const tree = useObjectTree()
const { refetch, isLoading } = tree.loadChild(props.item)
const router = useRouter()
const { show } = useConfirm()
const { trs } = useTranslate()
const auth = useAuth()

/* Элемент списка */
const treeItem = ref<HTMLElement>()

/* Вычисляем вкладку для перехода */
const activeLink = computed(() => {
  if (!auth.user || !auth.user.tabs.length) return ObjectTreeLink.MONITORING.link

  if (props.item?.alias === 'inventory') return ObjectTreeLink.INVENTORY.link

  for (let item of Object.keys(ObjectTreeLink)) {
    if (auth.user.tabs.includes(item)) return ObjectTreeLink[item].link
  }

  return ObjectTreeLink.MONITORING.link
})

/**
 * Отображаем путь до выбранного объекта и
 * прокручиваем список
 */

watch(
  () => tree.pathObject,
  () => {
    if (tree.pathObject.includes(props.item.id) && !open.value) {
      open.value = true
      refetch()
    }
  },
  {
    immediate: true,
  },
)

onMounted(() => {
  if (props.item.id === tree.selectObjectId && treeItem.value) {
    treeItem.value.scrollIntoView({ block: 'center' })
  }
})

/**
 * Функция обработки клика для получения потомков
 */
const clickOpenHandler = async () => {
  if (props.item.child_count && props.item.children.length === 0) {
    await refetch()
  }

  open.value = !open.value
}

/**
 * Функция обработки клика по элементу списка
 */
const clickHandler = (event: MouseEvent) => {
  if (event.metaKey || event.ctrlKey) {
    tree.addSelected(props.item)
  } else {
    router.push(`/objects/${props.item.alias}/${props.item.id}/${activeLink.value}`)
  }
}

/**
 * Вызов контекстного меню
 * @param event
 */
const contextHandler = (event: MouseEvent) => {
  tree.contextObject = props.item
  tree.contextPosition = {
    x: event.clientX,
    y: event.clientY,
  }
}

/* Блок перетаскивания объекта */
const { move } = useMoveModel()
let timeOut: number = 0
let timeToDrag: number = 0

/**
 * Обработчик начала перетаскивания
 * @param item
 */
const dragStartHandler = (item: ObjectTreeItemType) => {
  tree.dragObjects = [item]
  timeToDrag = Date.now()
}

/**
 * Обработчик окончания перетаскивания
 * @param item
 * @param elem
 */
const dropHandler = (item: ObjectTreeItemType, elem: HTMLElement | undefined) => {
  clearTimeout(timeOut)

  if (Date.now() - timeToDrag < 200) {
    tree.dragObjects = []
  }

  if (tree.dragObjects.length && !tree.dragObjects.find((object) => object.id == item.id)) {
    const target = item.type != 3 ? (props.parent as ObjectTreeItemType) : item
    const objects = [...(tree.selectObjects.length ? tree.selectObjects : tree.dragObjects)]

    show({
      confirmFunc: async () => {
        await move(target, objects)

        tree.selectObjects = []
        tree.dragObjects = []
        elem?.classList.remove('v-tree__name_over')
      },
      title: trs('itf.move_title'),
    })
  }
}

/**
 * Обработчик наведения на цель перетаскивания
 * @param elem
 */
const dragEnterHandler = async (elem: HTMLElement | undefined) => {
  if (!elem || !tree.dragObjects.length) return
  elem.classList.add('v-tree__name_over')

  timeOut = setTimeout(async () => {
    if (props.item.children.length) {
      open.value = true
    } else {
      await refetch()
      open.value = true
    }
  }, 2000)
}

/**
 * Обработчик ухода с цели перетаскивания
 * @param item
 */
const dragOutHandler = (item: HTMLElement | undefined) => {
  if (!item || !tree.dragObjects.length) return

  clearTimeout(timeOut)

  item.classList.remove('v-tree__name_over')
}

/**
 * Обработка клика колеса мыши
 * @param event
 */
const mouseDownHandler = (event: MouseEvent) => {
  if (event.button === 1) {
    const route = router.resolve(
      `/objects/${props.item.alias}/${props.item.id}/${activeLink.value}`,
    )

    window.open(route.href, '_blank')
  }
}
</script>

<template>
  <div class="v-tree__item">
    <div
      v-if="size(item.child_count)"
      class="v-tree__folder"
      :class="{ 'v-tree__folder_open': open }"
      @click="clickOpenHandler"
    >
      <VProgressCircular v-if="isLoading" color="white" indeterminate :size="12" :width="2" />

      <VIcons v-else type="arrow_right" />
    </div>

    <div
      ref="treeItem"
      class="v-tree__name"
      :class="{
        'v-tree__name_selected': item.id == tree.selectObjectId,
        'v-tree__name_grouping': tree.selectObjects.find((object) => object.id == item.id),
      }"
      @click.right.prevent="contextHandler"
      @click="clickHandler"
      @mousedown.prevent="dragStartHandler(item)"
      @mousedown="mouseDownHandler"
      @mouseenter="() => dragEnterHandler(treeItem)"
      @mouseout="dragOutHandler(treeItem)"
      @mouseup="dropHandler(item, treeItem)"
    >
      <ObjectsTreeIcon :item="item" />

      {{ item.name }}

      <ObjectsTreeCounter v-if="parent" :item="item" />
    </div>

    <div v-if="item.children && open" class="v-tree__children">
      <ObjectsTreeItem
        v-for="object in item.children"
        :parent="item"
        :item="object"
        :key="object.id"
      />
    </div>
  </div>
</template>
